import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LANGUAGE,
  LanguageCode,
} from '../../config/locale';
import i18n from '../../i18n';
import AppHeader from './AppHeader';
import AppFooter from './AppFooter';
import WorkspaceFooter from './WorkspaceFooter';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useLocation, useNavigate } from 'react-router-dom';
import LandingPromoBackgroundJobBanner from '../../features/backoffice/settings/landingPromotion/LandingPromoBackgroundJobBanner';
import {
  configureLandingPromoImageJobs,
  tryResumeLandingPromoServerJob,
} from '../../features/backoffice/settings/landingPromotion/utils/landingPromotionBackgroundJobStore';
import { getBackofficeSidebarItems } from '../../features/backoffice/shell/backofficeSidebar';
import WorkspaceScopeBadge from './WorkspaceScopeBadge';
import { isDepartmentScopedWorkspacePath as isDepartmentScopedPath } from '../../utils/workspaceDepartmentScope';
import { isWorkspacePath, toCanonicalWorkspacePath, wsPath } from '../../utils/siteHosts';
import {
  getSidebarGroupChildren,
  isSidebarGroup,
  type SidebarEntry,
  type SidebarGroup,
  type SidebarItem,
  type WorkspaceSearchItem,
} from './layoutTypes';

export type { SidebarEntry, SidebarGroup, SidebarItem, WorkspaceSearchItem } from './layoutTypes';
export { getSidebarGroupChildren, isSidebarGroup } from './layoutTypes';

const API_BASE_MAIN = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

function stripPathQuery(path: string): string {
  return path.split('?')[0];
}

function isItemActiveForPath(
  item: SidebarItem,
  activePath: string,
  stripQuery: (path: string) => string,
): boolean {
  const ap = activePath;
  const strippedActive = toCanonicalWorkspacePath(stripQuery(ap));

  if (Array.isArray(item.activePathExcludePrefixes) && item.activePathExcludePrefixes.length > 0) {
    const excluded = item.activePathExcludePrefixes.some(
      (prefix) => {
        const canonicalPrefix = toCanonicalWorkspacePath(prefix);
        return strippedActive === canonicalPrefix
          || strippedActive.startsWith(`${canonicalPrefix}/`);
      },
    );
    if (excluded) return false;
  }

  if (item.path.includes('?')) return ap === item.path;

  if (ap.includes('?')) {
    return strippedActive === toCanonicalWorkspacePath(stripQuery(item.path));
  }

  if (
    item.activePathPrefix
    && (() => {
      const canonicalPrefix = toCanonicalWorkspacePath(item.activePathPrefix);
      return strippedActive === canonicalPrefix
        || strippedActive.startsWith(`${canonicalPrefix}/`);
    })()
  ) {
    return true;
  }

  return strippedActive === toCanonicalWorkspacePath(stripQuery(item.path));
}

export function findActiveGroupIndex(
  groups: SidebarGroup[],
  activePath: string,
): number {
  const stripQuery = stripPathQuery;
  return groups.findIndex((group) =>
    getSidebarGroupChildren(group).some((child) =>
      isItemActiveForPath(child, activePath, stripQuery),
    ),
  );
}

type MainLayoutProps = {
  children: ReactNode;
  withSidebar?: boolean;
  forceWorkspaceChrome?: boolean;
  /** Flat list of items, or mixed list of items and groups (with sub-menus) */
  sidebarItems?: SidebarEntry[];
  /** Current path; used to highlight active item and expand the group containing it (when using groups) */
  activePath?: string;
};

function MainLayout({
  children,
  withSidebar,
  forceWorkspaceChrome = false,
  sidebarItems,
  activePath,
}: MainLayoutProps) {
  const { t } = useTranslation();
  const {
    isAuthenticated,
    auth,
    fetchPreferences,
    updatePreferences,
    authorizedFetch,
  } = useAuth();
  const { currency, setCurrency, supportedCurrencies } = useCurrency();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isWorkspaceRoute =
    forceWorkspaceChrome || isWorkspacePath(pathname);
  const showDepartmentScopeBadge =
    isWorkspaceRoute
    && isAuthenticated
    && isDepartmentScopedPath(pathname);
  const sidebarEnabled = withSidebar ?? isWorkspaceRoute;
  const resolvedSidebarItems = React.useMemo(
    () => (sidebarItems ?? (isWorkspaceRoute ? getBackofficeSidebarItems(t) : undefined)),
    [isWorkspaceRoute, sidebarItems, t],
  );

  React.useEffect(() => {
    if (!isWorkspaceRoute || !isAuthenticated) return;
    configureLandingPromoImageJobs(API_BASE_MAIN, (url, init) =>
      authorizedFetch(url as string, init),
    );
    tryResumeLandingPromoServerJob();
  }, [isWorkspaceRoute, isAuthenticated, authorizedFetch]);

  const initialLanguage: LanguageCode =
    (window.localStorage.getItem('lewouhmart_lang') as LanguageCode | null)
      ?? DEFAULT_LANGUAGE;

  const [language, setLanguage] =
    React.useState<LanguageCode>(initialLanguage);

  React.useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    fetchPreferences()
      .then((prefs) => {
        if (!prefs || cancelled) return;
        if (prefs.language) {
          const lang = prefs.language as LanguageCode;
          setLanguage(lang);
          window.localStorage.setItem('lewouhmart_lang', lang);
          i18n.changeLanguage(lang).catch(() => {});
        }
      })
      .catch(() => {
        // ignore preference load errors
      });

    return () => {
      cancelled = true;
    };
  }, [fetchPreferences, isAuthenticated]);

  const handleLanguageChange = (value: string) => {
    const lang = value as LanguageCode;
    setLanguage(lang);
    window.localStorage.setItem('lewouhmart_lang', lang);
    i18n.changeLanguage(lang).catch(() => {});
    if (isAuthenticated) {
      updatePreferences({ language: lang }).catch(() => {});
    }
  };

  const hasPermission = React.useCallback((anyOf?: string[]) => {
    if (!anyOf || anyOf.length === 0) return true;
    if (auth.type === 'ADMIN') return true;
    const perms = auth.permissions || [];
    return anyOf.some((k) => perms.includes(k));
  }, [auth.permissions, auth.type]);

  const filteredSidebarItems = React.useMemo<SidebarEntry[] | undefined>(() => {
    if (!resolvedSidebarItems) return resolvedSidebarItems;
    return resolvedSidebarItems
      .map((entry) => {
        if (!isSidebarGroup(entry)) {
          const item = entry as SidebarItem;
          return hasPermission(item.requiredAnyOf) ? item : null;
        }
        const group = entry as SidebarGroup;
        if (!hasPermission(group.requiredAnyOf)) return null;
        if (group.sections?.length) {
          const sections = group.sections
            .map((section) => ({
              ...section,
              children: section.children.filter((c) => hasPermission(c.requiredAnyOf)),
            }))
            .filter((section) => section.children.length > 0);
          if (sections.length === 0) return null;
          return { ...group, sections };
        }
        const children = (group.children ?? []).filter((c) => hasPermission(c.requiredAnyOf));
        if (children.length === 0) return null;
        return { ...group, children };
      })
      .filter(Boolean) as SidebarEntry[];
  }, [hasPermission, resolvedSidebarItems]);

  const [appParamsConfigured, setAppParamsConfigured] = React.useState<boolean | null>(null);
  const [departmentsConfigured, setDepartmentsConfigured] = React.useState<boolean | null>(null);
  const [siteContentConfigured, setSiteContentConfigured] = React.useState<boolean | null>(null);
  const [budgetConfigured, setBudgetConfigured] = React.useState<boolean | null>(null);
  const [mailRoutingConfigured, setMailRoutingConfigured] = React.useState<boolean | null>(null);
  const [deliveryPricingConfigured, setDeliveryPricingConfigured] =
    React.useState<boolean | null>(null);
  React.useEffect(() => {
    if (!isWorkspaceRoute || !isAuthenticated) return;
    let cancelled = false;
    const run = async () => {
      try {
        const canAccessDepartmentSetup =
          auth.type === 'ADMIN' ||
          hasPermission([
            'DEPARTMENT:READ',
            'DEPARTMENT:CREATE',
            'DEPARTMENT:UPDATE',
          ]);
        const [paramsRes, siteContentRes, departmentsRes] = await Promise.all([
          authorizedFetch(`${API_BASE_MAIN}/settings/app-parameters`, {
            headers: { 'x-department-slug': '' },
          }),
          authorizedFetch(`${API_BASE_MAIN}/settings/site-content`, {
            headers: { 'x-department-slug': '' },
          }),
          canAccessDepartmentSetup
            ? authorizedFetch(`${API_BASE_MAIN}/departments/workspace`, {
                headers: { 'x-department-slug': '' },
              })
            : Promise.resolve(
                new Response(JSON.stringify([]), {
                  status: 200,
                  headers: { 'Content-Type': 'application/json' },
                }),
              ),
        ]);
        if (!paramsRes.ok || !siteContentRes.ok || !departmentsRes.ok) {
          throw new Error('status');
        }
        const paramsData = (await paramsRes.json()) as { configured?: boolean };
        const departmentsData = (await departmentsRes.json()) as unknown[];
        const siteContentData = (await siteContentRes.json()) as Partial<{
          publicContactCountry: string;
          publicContactCity: string;
          publicContactAddress: string;
          publicContactEmail: string;
          publicContactPhone: string;
        }>;
        const contentReady =
          String(siteContentData.publicContactCountry || '').trim().length > 0 &&
          String(siteContentData.publicContactCity || '').trim().length > 0 &&
          String(siteContentData.publicContactAddress || '').trim().length > 0 &&
          String(siteContentData.publicContactPhone || '').trim().length > 0 &&
          String(siteContentData.publicContactEmail || '')
            .trim()
            .includes('@');
        const departmentsReady = canAccessDepartmentSetup
          ? Array.isArray(departmentsData) && departmentsData.length > 0
          : true;
        let budgetReady = true;
        const canAccessBudget =
          auth.type === 'ADMIN' ||
          hasPermission([
            'BUDGET_PERIOD:READ',
            'BUDGET_PERIOD:CREATE',
            'BUDGET_PERIOD:UPDATE',
          ]);
        if (canAccessBudget) {
          const budgetRes = await authorizedFetch(
            `${API_BASE_MAIN}/budget/periods?page=1&pageSize=1`,
          );
          if (!budgetRes.ok) throw new Error('budget_status');
          const budgetData = (await budgetRes.json()) as Partial<{
            items: unknown[];
          }>;
          budgetReady = Array.isArray(budgetData.items) && budgetData.items.length > 0;
        }
        let mailRoutingReady = true;
        const canAccessMailRouting =
          auth.type === 'ADMIN' || hasPermission(['MAIL_ROUTING:READ']);
        if (canAccessMailRouting) {
          const mailRoutingRes = await authorizedFetch(
            `${API_BASE_MAIN}/settings/mail-routing`,
            {
              headers: { 'x-department-slug': '' },
            },
          );
          if (!mailRoutingRes.ok) throw new Error('mail_routing_status');
          const mailRoutingData = (await mailRoutingRes.json()) as Partial<{
            config: Record<
              string,
              {
                fromEmails?: string[];
                replyTo?: string;
              }
            >;
          }>;
          const config = mailRoutingData.config || {};
          mailRoutingReady = Object.values(config).some((entry) => {
            const fromCount = Array.isArray(entry?.fromEmails)
              ? entry.fromEmails.filter((x) => String(x).includes('@')).length
              : 0;
            const hasReplyTo = String(entry?.replyTo || '').includes('@');
            return fromCount > 0 || hasReplyTo;
          });
        }
        let deliveryPricingReady = true;
        const canAccessDeliveryPricing =
          auth.type === 'ADMIN' ||
          hasPermission(['DELIVERY_PRICING:READ', 'DELIVERY_PRICING:UPDATE']);
        if (canAccessDeliveryPricing) {
          const deliveryPricingRes = await authorizedFetch(
            `${API_BASE_MAIN}/delivery-pricing`,
            {
              headers: { 'x-department-slug': '' },
            },
          );
          if (!deliveryPricingRes.ok) throw new Error('delivery_pricing_status');
          const deliveryPricingData = (await deliveryPricingRes.json()) as Partial<{
            pricing: {
              storeCountry?: string;
              countries?: Array<{ countryCode?: string; enabled?: boolean }>;
            };
          }>;
          const storeCountry = String(
            deliveryPricingData.pricing?.storeCountry || '',
          )
            .trim()
            .toUpperCase();
          const countries = Array.isArray(deliveryPricingData.pricing?.countries)
            ? deliveryPricingData.pricing?.countries || []
            : [];
          const hasAtLeastOneCountry = countries.length > 0;
          const hasStoreCountryPricing = countries.some((c) => {
            const code = String(c.countryCode || '')
              .trim()
              .toUpperCase();
            if (!code || code !== storeCountry) return false;
            return c.enabled !== false;
          });
          deliveryPricingReady =
            hasAtLeastOneCountry && Boolean(storeCountry) && hasStoreCountryPricing;
        }
        if (!cancelled) {
          setDepartmentsConfigured(departmentsReady);
          setAppParamsConfigured(Boolean(paramsData.configured));
          setSiteContentConfigured(contentReady);
          setBudgetConfigured(budgetReady);
          setMailRoutingConfigured(mailRoutingReady);
          setDeliveryPricingConfigured(deliveryPricingReady);
        }
      } catch {
        if (!cancelled) {
          setDepartmentsConfigured(true);
          setAppParamsConfigured(true);
          setSiteContentConfigured(true);
          setBudgetConfigured(true);
          setMailRoutingConfigured(true);
          setDeliveryPricingConfigured(true);
        }
      }
    };
    void run();
    const interval = window.setInterval(() => {
      void run();
    }, 4000);
    const onFocus = () => {
      void run();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [
    auth.type,
    authorizedFetch,
    hasPermission,
    isAuthenticated,
    isWorkspaceRoute,
    pathname,
  ]);

  const lockStage = React.useMemo<
    | 'none'
    | 'departments'
    | 'params'
    | 'site-content'
    | 'budget'
    | 'mail-routing'
    | 'delivery-pricing'
  >(() => {
    if (!isWorkspaceRoute) return 'none';
    if (departmentsConfigured === false) return 'departments';
    if (appParamsConfigured === false) return 'params';
    if (appParamsConfigured === true && siteContentConfigured === false) {
      return 'site-content';
    }
    if (
      appParamsConfigured === true &&
      siteContentConfigured === true &&
      budgetConfigured === false
    ) {
      return 'budget';
    }
    if (
      appParamsConfigured === true &&
      siteContentConfigured === true &&
      budgetConfigured === true &&
      mailRoutingConfigured === false
    ) {
      return 'mail-routing';
    }
    if (
      appParamsConfigured === true &&
      siteContentConfigured === true &&
      budgetConfigured === true &&
      mailRoutingConfigured === true &&
      deliveryPricingConfigured === false
    ) {
      return 'delivery-pricing';
    }
    return 'none';
  }, [
    appParamsConfigured,
    budgetConfigured,
    deliveryPricingConfigured,
    departmentsConfigured,
    isWorkspaceRoute,
    mailRoutingConfigured,
    siteContentConfigured,
  ]);

  const setupProgress = React.useMemo(() => {
    const total = 6;
    if (lockStage === 'departments') {
      return {
        step: 1,
        total,
        label: 'Setup required: create at least one department in Department management',
        nextPath: wsPath("/workspace/department-management"),
        nextCta: 'Open Department management',
      };
    }
    if (lockStage === 'params') {
      return {
        step: 2,
        total,
        label: 'Setup required: App parameters',
        nextPath: wsPath("/workspace/app-parameters"),
        nextCta: 'Open App parameters',
      };
    }
    if (lockStage === 'site-content') {
      return {
        step: 3,
        total,
        label:
          'Setup required: Site content (public contact country, city, address, phone, email)',
        nextPath: wsPath("/workspace/site-content"),
        nextCta: 'Open Site content',
      };
    }
    if (lockStage === 'budget') {
      return {
        step: 4,
        total,
        label: 'Setup required: Budget (create at least one budget period)',
        nextPath: wsPath("/workspace/budget"),
        nextCta: 'Open Budget',
      };
    }
    if (lockStage === 'mail-routing') {
      return {
        step: 5,
        total,
        label:
          'Setup required: Email sender (configure at least one sender/reply-to in Mail routing)',
        nextPath: wsPath("/workspace/mail-routing"),
        nextCta: 'Open Mail routing',
      };
    }
    if (lockStage === 'delivery-pricing') {
      return {
        step: 6,
        total,
        label:
          'Setup required: Delivery pricing (configure at least one country, including the store/original country)',
        nextPath: wsPath("/workspace/delivery-pricing"),
        nextCta: 'Open Delivery pricing',
      };
    }
    return {
      step: total,
      total,
      label: 'Initial setup completed',
      nextPath: null,
      nextCta: null,
    };
  }, [lockStage]);

  const lockExemptPaths = React.useMemo(() => {
    if (lockStage === 'departments') return [wsPath('/workspace/department-management')];
    if (lockStage === 'params') return [wsPath('/workspace/app-parameters')];
    if (lockStage === 'site-content') {
      return [
        wsPath('/workspace/department-management'),
        wsPath('/workspace/app-parameters'),
        wsPath('/workspace/site-content'),
      ];
    }
    if (lockStage === 'budget') {
      return [
        wsPath('/workspace/department-management'),
        wsPath('/workspace/app-parameters'),
        wsPath('/workspace/site-content'),
        wsPath('/workspace/budget'),
      ];
    }
    if (lockStage === 'mail-routing') {
      return [
        wsPath('/workspace/department-management'),
        wsPath('/workspace/app-parameters'),
        wsPath('/workspace/site-content'),
        wsPath('/workspace/budget'),
        wsPath('/workspace/mail-routing'),
      ];
    }
    if (lockStage === 'delivery-pricing') {
      return [
        wsPath('/workspace/department-management'),
        wsPath('/workspace/app-parameters'),
        wsPath('/workspace/site-content'),
        wsPath('/workspace/budget'),
        wsPath('/workspace/mail-routing'),
        wsPath('/workspace/delivery-pricing'),
      ];
    }
    return [] as string[];
  }, [lockStage]);

  const isExemptPath = React.useCallback(
    (path: string) =>
      lockExemptPaths.some(
        (p) => path === p || path.startsWith(`${p}/`),
      ),
    [lockExemptPaths],
  );
  const effectiveSidebarItems = React.useMemo<SidebarEntry[] | undefined>(() => {
    if (!filteredSidebarItems) return filteredSidebarItems;
    if (lockStage === 'none') return filteredSidebarItems;
    return filteredSidebarItems.map((entry) => {
      if (!isSidebarGroup(entry)) {
        return {
          ...entry,
          disabled: !isExemptPath(entry.path),
        };
      }
      if (entry.sections?.length) {
        return {
          ...entry,
          sections: entry.sections.map((section) => ({
            ...section,
            children: section.children.map((child) => ({
              ...child,
              disabled: !isExemptPath(child.path),
            })),
          })),
        };
      }
      return {
        ...entry,
        children: (entry.children ?? []).map((child) => ({
          ...child,
          disabled: !isExemptPath(child.path),
        })),
      };
    });
  }, [filteredSidebarItems, isExemptPath, lockStage]);
  const setupGuidanceTooltip = React.useMemo(() => {
    if (lockStage === 'departments') {
      return 'Complete setup step 1/6: create at least one department in Department management.';
    }
    if (lockStage === 'params') {
      return 'Complete setup step 2/6: fill and save App parameters.';
    }
    if (lockStage === 'site-content') {
      return 'Complete setup step 3/6: fill and save Site content public contact fields.';
    }
    if (lockStage === 'budget') {
      return 'Complete setup step 4/6: create at least one Budget period.';
    }
    if (lockStage === 'mail-routing') {
      return 'Complete setup step 5/6: configure at least one email sender or reply-to in Mail routing.';
    }
    if (lockStage === 'delivery-pricing') {
      return 'Complete setup step 6/6: configure at least one enabled country rate including the store/original country in Delivery pricing.';
    }
    return undefined;
  }, [lockStage]);
  const showFixedSetupBanner = isWorkspaceRoute && lockStage !== 'none';
  const setupBannerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!showFixedSetupBanner) {
      document.documentElement.style.setProperty('--workspace-setup-banner-h', '0px');
      return undefined;
    }
    const el = setupBannerRef.current;
    if (!el) return undefined;
    const syncBannerHeight = () => {
      document.documentElement.style.setProperty(
        '--workspace-setup-banner-h',
        `${el.offsetHeight}px`,
      );
    };
    syncBannerHeight();
    const observer = new ResizeObserver(syncBannerHeight);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty('--workspace-setup-banner-h', '0px');
    };
  }, [showFixedSetupBanner]);

  React.useEffect(() => {
    if (showDepartmentScopeBadge) return undefined;
    document.documentElement.style.setProperty('--workspace-scope-badge-h', '0px');
    return undefined;
  }, [showDepartmentScopeBadge]);

  const workspaceChromeOffset =
    'calc(var(--workspace-header-h, 64px) + var(--workspace-setup-banner-h, 0px) + var(--workspace-scope-badge-h, 0px))';
  const workspaceChromeInset =
    'calc(100vh - var(--workspace-header-h, 64px) - var(--workspace-setup-banner-h, 0px) - var(--workspace-scope-badge-h, 0px))';
  // Scope badge sits in the main column only (md:left-56), not over the sidebar.
  const workspaceSidebarTop =
    'calc(var(--workspace-header-h, 64px) + var(--workspace-setup-banner-h, 0px))';
  const workspaceSidebarHeight =
    'calc(100vh - var(--workspace-header-h, 64px) - var(--workspace-setup-banner-h, 0px))';

  const hasSidebar = Boolean(sidebarEnabled && effectiveSidebarItems && effectiveSidebarItems.length > 0);
  const workspaceSearchItems = React.useMemo<WorkspaceSearchItem[]>(() => {
    if (!effectiveSidebarItems) return [];
    const out: WorkspaceSearchItem[] = [];
    for (const entry of effectiveSidebarItems) {
      if (isSidebarGroup(entry)) {
        for (const child of getSidebarGroupChildren(entry)) {
          out.push({ label: child.label, path: child.path, groupLabel: entry.label });
        }
      } else {
        out.push({ label: entry.label, path: entry.path });
      }
    }
    return out;
  }, [effectiveSidebarItems]);

  const stripQuery = React.useCallback((path: string) => path.split('?')[0], []);

  const sidebarGroups = React.useMemo<SidebarGroup[]>(() => {
    if (!effectiveSidebarItems) return [];
    return effectiveSidebarItems.filter(isSidebarGroup);
  }, [effectiveSidebarItems]);

  const routeActiveGroupIndex = React.useMemo(() => {
    if (!hasSidebar || sidebarGroups.length === 0) return -1;
    return findActiveGroupIndex(sidebarGroups, activePath ?? '');
  }, [activePath, hasSidebar, sidebarGroups]);

  const [selectedGroupIndex, setSelectedGroupIndex] = React.useState(0);
  const [sidebarBadgeCounts, setSidebarBadgeCounts] = React.useState<Record<string, number>>({});

  React.useEffect(() => {
    if (routeActiveGroupIndex >= 0) {
      setSelectedGroupIndex(routeActiveGroupIndex);
    }
  }, [routeActiveGroupIndex]);

  const activeGroupIndex =
    selectedGroupIndex >= 0 && selectedGroupIndex < sidebarGroups.length
      ? selectedGroupIndex
      : Math.max(routeActiveGroupIndex, 0);

  const activeSidebarGroup = sidebarGroups[activeGroupIndex] ?? null;

  const handleWorkspaceGroupSelect = React.useCallback(
    (index: number) => {
      if (index < 0 || index >= sidebarGroups.length) return;
      setSelectedGroupIndex(index);
      const group = sidebarGroups[index];
      const ap = activePath ?? '';
      const groupChildren = getSidebarGroupChildren(group);
      const alreadyInGroup = groupChildren.some((child) =>
        isItemActiveForPath(child, ap, stripQuery),
      );
      if (!alreadyInGroup) {
        const firstEnabled = groupChildren.find((child) => !child.disabled);
        if (firstEnabled) navigate(firstEnabled.path);
      }
    },
    [activePath, navigate, sidebarGroups, stripQuery],
  );

  React.useEffect(() => {
    if (!isWorkspaceRoute || !isAuthenticated) return;
    const canReadOrders = auth.type === 'ADMIN' || (auth.permissions || []).includes('ORDER:READ');
    if (!canReadOrders) return;

    let cancelled = false;
    const loadPendingShipCount = async () => {
      try {
        const res = await authorizedFetch(`${API_BASE_MAIN}/sales/orders/stats`);
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as {
          byStatus?: Record<string, number>;
          preorders?: { open?: number };
        };
        const by = data.byStatus || {};
        const pendingToShip =
          Number(by.PAID || 0) +
          Number(by.RESHIP_PENDING || 0) +
          Number(data.preorders?.open || 0);
        if (!cancelled) {
          setSidebarBadgeCounts((prev) => ({
            ...prev,
            orders_pending_ship: Math.max(0, pendingToShip),
          }));
        }
      } catch {
        // ignore transient errors for sidebar badges
      }
    };

    void loadPendingShipCount();
    const loadOpenFeedbackCount = async () => {
      try {
        const res = await authorizedFetch(`${API_BASE_MAIN}/customer/feedback/inbox?scope=ACTIVE`);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ id: string }>;
        if (!cancelled) {
          setSidebarBadgeCounts((prev) => ({
            ...prev,
            feedback_open: Math.max(0, Array.isArray(rows) ? rows.length : 0),
          }));
        }
      } catch {
        // ignore transient errors for sidebar badges
      }
    };

    void loadOpenFeedbackCount();
    const timer = window.setInterval(() => {
      void loadPendingShipCount();
      void loadOpenFeedbackCount();
    }, 30000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [isWorkspaceRoute, isAuthenticated, auth.type, auth.permissions, authorizedFetch]);

  const isActive = (
    path: string,
    activePathPrefix?: string,
    activePathExcludePrefixes?: string[],
  ): boolean => {
    if (activePath !== undefined) {
      const activeHasQuery = activePath.includes('?');
      const pathHasQuery = path.includes('?');
      const strippedActive = stripQuery(activePath);

      if (Array.isArray(activePathExcludePrefixes) && activePathExcludePrefixes.length > 0) {
        const excluded = activePathExcludePrefixes.some(
          (prefix) => strippedActive === prefix || strippedActive.startsWith(`${prefix}/`),
        );
        if (excluded) return false;
      }

      // Query-aware routes (e.g. /workspace/orders?view=INCIDENTS) must match exactly.
      if (pathHasQuery) return activePath === path;

      // If URL has query params but there is no exact query-specific sidebar item,
      // keep the base route highlighted (e.g. /workspace/orders?view=ACTIVE -> /workspace/orders).
      if (activeHasQuery) {
        const hasExactQuerySidebarItem = (effectiveSidebarItems || []).some((entry) => {
          if (isSidebarGroup(entry)) {
            return getSidebarGroupChildren(entry).some(
              (c) => c.path.includes('?') && c.path === activePath,
            );
          }
          return entry.path.includes('?') && entry.path === activePath;
        });
        if (hasExactQuerySidebarItem) return false;
      }

      if (
        activePathPrefix &&
        (strippedActive === activePathPrefix ||
          strippedActive.startsWith(`${activePathPrefix}/`))
      ) {
        return true;
      }

      return strippedActive === stripQuery(path);
    }
    for (const entry of effectiveSidebarItems!) {
      if (!isSidebarGroup(entry) && entry.path === path) return Boolean(entry.active);
      if (isSidebarGroup(entry)) {
        const c = getSidebarGroupChildren(entry).find((ch) => ch.path === path);
        if (c) return Boolean(c.active);
      }
    }
    return false;
  };

  const renderSidebarItem = (child: SidebarItem) => {
    const active = isActive(
      child.path,
      child.activePathPrefix,
      child.activePathExcludePrefixes,
    );
    return (
      <button
        key={child.path}
        type="button"
        onClick={() => {
          if (!child.disabled) navigate(child.path);
        }}
        disabled={child.disabled}
        title={child.disabled ? setupGuidanceTooltip : undefined}
        className={`flex w-full items-center gap-2.5 border-l-4 px-3 py-2.5 text-left text-sm transition ${
          child.disabled ? 'cursor-not-allowed opacity-45' : ''
        } ${
          active
            ? 'border-sky-400 bg-sky-700 text-white'
            : 'border-transparent text-slate-300 hover:bg-slate-800/90 hover:text-white'
        }`}
      >
        {child.icon && (
          <span className="flex shrink-0 [&>svg]:h-4 [&>svg]:w-4">
            {child.icon}
          </span>
        )}
        <span className="font-medium">{child.label}</span>
        {child.badgeKey && (sidebarBadgeCounts[child.badgeKey] || 0) > 0 ? (
          <span className="ml-auto inline-flex min-w-[1.1rem] items-center justify-center bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
            {sidebarBadgeCounts[child.badgeKey] > 99 ? '99+' : sidebarBadgeCounts[child.badgeKey]}
          </span>
        ) : null}
      </button>
    );
  };

  const renderSidebarContent = () => {
    if (!activeSidebarGroup) return null;
    if (activeSidebarGroup.sections?.length) {
      return activeSidebarGroup.sections.map((section, sectionIdx) => (
        <div key={section.label}>
          {sectionIdx > 0 ? <div className="mx-3 my-1 border-t border-slate-800" /> : null}
          <p className="px-4 pb-1 pt-2.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">
            {section.label}
          </p>
          {section.children.map((child) => renderSidebarItem(child))}
        </div>
      ));
    }
    return (activeSidebarGroup.children ?? []).map((child) => renderSidebarItem(child));
  };

  return (
    <div
      className={`flex min-h-screen min-w-0 flex-col overflow-x-clip text-slate-900 ${
        isWorkspaceRoute ? 'workspace-ui bg-slate-100' : 'bg-slate-50'
      }`}
      style={{
        paddingTop: isWorkspaceRoute
          ? workspaceChromeOffset
          : 'var(--workspace-header-h, 4rem)',
      }}
    >
      <AppHeader
        language={language}
        currency={currency}
        supportedCurrencies={supportedCurrencies}
        onLanguageChange={handleLanguageChange}
        onCurrencyChange={setCurrency}
        showWorkspaceNav={isWorkspaceRoute}
        hasSidebar={hasSidebar}
        workspaceSearchItems={workspaceSearchItems}
        workspaceNavGroups={hasSidebar ? sidebarGroups : undefined}
        activeWorkspaceGroupIndex={hasSidebar ? activeGroupIndex : undefined}
        onWorkspaceGroupSelect={hasSidebar ? handleWorkspaceGroupSelect : undefined}
        workspaceNavBadgeCounts={hasSidebar ? sidebarBadgeCounts : undefined}
      />

      {showFixedSetupBanner ? (
        <div
          ref={setupBannerRef}
          className="fixed left-0 right-0 z-30 border-b border-amber-200 bg-amber-50/95 backdrop-blur"
          style={{ top: 'var(--workspace-header-h, 4rem)' }}
        >
          <div
            className={`flex w-full items-center gap-3 py-2.5 ${
              hasSidebar
                ? 'px-3 sm:px-4 md:ml-56'
                : 'mx-auto max-w-6xl px-4 sm:px-6 lg:px-8'
            }`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                  App setup progress
                </span>
                <span className="bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                  Step {setupProgress.step}/{setupProgress.total}
                </span>
              </div>
              <p className="mt-0.5 truncate text-xs text-amber-900/90">
                {setupProgress.label}
              </p>
            </div>
            <div className="h-1.5 w-28 overflow-hidden bg-amber-200">
              <div
                className="h-full bg-amber-700 transition-all"
                style={{
                  width: `${Math.max(
                    5,
                    Math.min(
                      100,
                      (setupProgress.step / setupProgress.total) * 100,
                    ),
                  )}%`,
                }}
              />
            </div>
            {setupProgress.nextPath ? (
              <button
                type="button"
                onClick={() => navigate(setupProgress.nextPath as string)}
                className="inline-flex shrink-0 items-center justify-center border border-amber-700 bg-amber-700 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-amber-800"
              >
                {setupProgress.nextCta || 'Open next step'}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {showDepartmentScopeBadge ? (
        <WorkspaceScopeBadge
          accessToken={auth.accessToken}
          hasSidebar={hasSidebar}
          isAdmin={auth.type === 'ADMIN'}
        />
      ) : null}

      <main className="flex min-w-0 flex-1 overflow-x-clip">
        {hasSidebar && activeSidebarGroup && (
          <aside
            className="fixed left-0 hidden w-56 overflow-y-auto border-r border-slate-800 bg-slate-900 md:block"
            style={{
              top: workspaceSidebarTop,
              height: workspaceSidebarHeight,
            }}
          >
            <div className="border-b border-slate-800 bg-slate-900 px-4 py-3">
              <h2 className="text-sm font-bold text-white">{activeSidebarGroup.label}</h2>
            </div>
            <nav className="py-1 font-medium">
              {renderSidebarContent()}
            </nav>
          </aside>
        )}

        <div
          className={`flex min-w-0 max-w-full flex-1 flex-col overflow-x-clip bg-white ${
            hasSidebar ? 'md:ml-56' : ''
          } ${hasSidebar ? 'pt-3' : 'pt-6'}`}
          style={{
            minHeight: isWorkspaceRoute
              ? workspaceChromeInset
              : 'calc(100vh - var(--workspace-header-h, 4rem))',
            paddingBottom: 'calc(0.75rem + var(--landing-promo-job-pad, 0px))',
          }}
        >
          <div
            className={
              isWorkspaceRoute
                ? 'flex w-full min-w-0 flex-1 flex-col px-3 sm:px-4'
                : 'mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 sm:px-6 lg:px-8'
            }
          >
            {children}
          </div>
        </div>
      </main>

      {isWorkspaceRoute ? <WorkspaceFooter hasSidebar={hasSidebar} /> : <AppFooter />}

      {isWorkspaceRoute ? <LandingPromoBackgroundJobBanner /> : null}
    </div>
  );
}

export default MainLayout;

